import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, Divider, IconButton, TextField, Tooltip } from '@mui/material'
import { PageTitle } from '../../components/PageElements'
import { useNotification } from '../../hooks/useNotification'
import services from '../../services'
import RelatorioHtmlFrame, { ContextoRelatorio } from '../../components/RelatorioHtmlFrame'

interface Tokens {
  corPrimaria: string; corDestaque: string; corSecundaria: string; fundo: string; superficie: string
  texto: string; textoSuave: string; borda: string; positivo: string; alerta: string; negativo: string
  fonte: string; fonteUrl?: string | null; raio: number; espaco: number; paleta: string[]
}
interface Template {
  id?: number | null; nome: string; descricao?: string | null; tokens: Tokens; css: string
  logo?: string | null; padrao?: boolean
}
interface Resumo {
  id?: number | null; nome: string; descricao?: string | null; padrao: boolean; relatorios: number
  atualizadoEm?: string | null
}

const CORES: { chave: keyof Tokens; rotulo: string; dica: string }[] = [
  { chave: 'corPrimaria', rotulo: 'Primária', dica: 'Destaques, série principal, borda dos cartões' },
  { chave: 'corDestaque', rotulo: 'Destaque', dica: 'Segunda cor da marca, com moderação' },
  { chave: 'corSecundaria', rotulo: 'Secundária', dica: 'Comparação, previsto, segunda série' },
  { chave: 'fundo', rotulo: 'Fundo', dica: 'Fundo da página' },
  { chave: 'superficie', rotulo: 'Superfície', dica: 'Fundo de cartões e painéis' },
  { chave: 'texto', rotulo: 'Texto', dica: 'Texto principal' },
  { chave: 'textoSuave', rotulo: 'Texto suave', dica: 'Rótulos, legendas, notas' },
  { chave: 'borda', rotulo: 'Borda', dica: 'Bordas e linhas de tabela' },
  { chave: 'positivo', rotulo: 'Positivo', dica: 'Resultado bom' },
  { chave: 'alerta', rotulo: 'Alerta', dica: 'Atenção' },
  { chave: 'negativo', rotulo: 'Negativo', dica: 'Resultado ruim' }
]

const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

/** O input de cor do navegador só aceita #rrggbb. */
const seisDigitos = (cor: string) =>
  /^#[0-9a-fA-F]{3}$/.test(cor) ? '#' + cor.slice(1).split('').map(c => c + c).join('') : HEX.test(cor) ? cor : '#000000'

function CampoCor({ rotulo, dica, valor, aoMudar }: { rotulo: string; dica?: string; valor: string; aoMudar: (v: string) => void }) {
  return (
    <Tooltip title={dica ?? ''} placement="top">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <input type="color" value={seisDigitos(valor)} onChange={e => aoMudar(e.target.value)}
               style={{ width: 36, height: 36, border: '1px solid #e2e8f0', borderRadius: 6, padding: 2, background: '#fff' }} />
        <TextField size="small" label={rotulo} value={valor} onChange={e => aoMudar(e.target.value.trim())}
                   error={!HEX.test(valor)} inputProps={{ maxLength: 7 }} sx={{ width: 130 }} />
      </Box>
    </Tooltip>
  )
}

/**
 * Templates visuais dos relatórios HTML.
 *
 * O template é injetado pelo portal em todo relatório ligado a ele, inclusive nos já
 * publicados — por isso a prévia ao lado, e por isso só o administrador do BI salva.
 */
const TemplatesHtml = () => {
  const { notify } = useNotification()
  const [lista, setLista] = useState<Resumo[]>([])
  const [selecionado, setSelecionado] = useState<number | null | undefined>(undefined)
  const [template, setTemplate] = useState<Template | null>(null)
  const [cssDoPortal, setCssDoPortal] = useState('')
  const [previa, setPrevia] = useState<{ html: string; contexto: ContextoRelatorio } | null>(null)
  const [erroPrevia, setErroPrevia] = useState('')
  const [salvando, setSalvando] = useState(false)
  const arquivoLogo = useRef<HTMLInputElement>(null)

  const carregarLista = useCallback(async () => {
    const r: any = await services.api.templatesHtml()
    if (r.fail) notify(r.error, { variant: 'error' })
    else setLista(r.data)
  }, [notify])

  useEffect(() => {
    carregarLista()
    ;(async () => {
      const r: any = await services.api.templateHtml(null)
      if (!r.fail) setCssDoPortal(r.data.css)
    })()
  }, [carregarLista])

  const abrir = async (id: number | null) => {
    const r: any = await services.api.templateHtml(id)
    if (r.fail) {
      notify(r.error, { variant: 'error' })
      return
    }
    setSelecionado(id)
    setTemplate(r.data)
  }

  const novo = async () => {
    const r: any = await services.api.templateHtml(null)
    if (r.fail) return
    setSelecionado(undefined)
    setTemplate({ ...r.data, id: null, nome: 'Novo template', descricao: '', padrao: false })
  }

  // A prévia acompanha a edição, com uma pausa para não chamar o servidor a cada tecla.
  useEffect(() => {
    if (!template) return
    const espera = setTimeout(async () => {
      const r: any = await services.api.templateHtmlPrevia(template)
      if (r.fail) setErroPrevia(r.error)
      else {
        setErroPrevia('')
        setPrevia(r.data)
      }
    }, 600)
    return () => clearTimeout(espera)
  }, [template])

  const mudar = (parte: Partial<Template>) => setTemplate(t => (t ? { ...t, ...parte } : t))
  const mudarToken = (parte: Partial<Tokens>) => setTemplate(t => (t ? { ...t, tokens: { ...t.tokens, ...parte } } : t))

  const doPortal = template != null && template.id == null && selecionado === null
  const salvar = async () => {
    if (!template) return
    setSalvando(true)
    const r: any = await services.api.templateHtmlSalvar(template.id ?? null, template)
    setSalvando(false)
    if (r.fail) {
      notify(r.error, { variant: 'error' })
      return
    }
    notify('Template salvo. Os relatórios ligados a ele já abrem com o visual novo.', { variant: 'success' })
    setTemplate(r.data)
    setSelecionado(r.data.id)
    carregarLista()
  }

  const definirPadrao = async (id: number | null) => {
    const r: any = await services.api.templateHtmlDefinirPadrao(id)
    if (r.fail) notify(r.error, { variant: 'error' })
    else {
      notify('Template padrão atualizado.', { variant: 'success' })
      carregarLista()
      if (template) mudar({ padrao: template.id === id })
    }
  }

  const excluir = async () => {
    if (!template?.id) return
    const resumo = lista.find(l => l.id === template.id)
    const aviso = resumo?.relatorios
      ? `${resumo.relatorios} relatório(s) usam este template e vão passar a usar o padrão. Excluir?`
      : 'Excluir este template?'
    if (!window.confirm(aviso)) return
    const r: any = await services.api.templateHtmlExcluir(template.id)
    if (r.fail) notify(r.error, { variant: 'error' })
    else {
      notify('Template excluído.', { variant: 'success' })
      setTemplate(null)
      setPrevia(null)
      setSelecionado(undefined)
      carregarLista()
    }
  }

  const lerLogo = (arquivo?: File) => {
    if (!arquivo) return
    if (arquivo.size > 220_000) {
      notify('O logo precisa ter até 220 KB. Prefira SVG ou PNG pequeno.', { variant: 'error' })
      return
    }
    const leitor = new FileReader()
    leitor.onload = () => mudar({ logo: String(leitor.result) })
    leitor.readAsDataURL(arquivo)
  }

  const t = template?.tokens

  return (
    <div className="wrapper-page-content">
      <PageTitle subText="Base visual dos relatórios HTML: cores, fonte e componentes">
        <div>Templates HTML</div>
      </PageTitle>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '260px 1fr' }, gap: 3, p: 3 }}>
        <Box>
          <Button fullWidth variant="contained" className="btn-purple" onClick={novo} sx={{ mb: 2 }}>Novo template</Button>
          {lista.map(item => (
            <Box key={item.id ?? 'portal'} onClick={() => abrir(item.id ?? null)}
                 sx={{
                   p: 1.5, mb: 1, borderRadius: 1, cursor: 'pointer', border: '1px solid',
                   borderColor: (item.id ?? null) === selecionado ? 'primary.main' : '#e2e8f0', background: '#fff'
                 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <strong style={{ fontSize: 14 }}>{item.nome}</strong>
                {item.padrao && <Chip size="small" color="primary" label="Padrão" />}
              </Box>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                {item.id == null ? 'Embutido no portal' : `${item.relatorios} relatório(s) escolheram este`}
              </div>
            </Box>
          ))}
          <Alert severity="info" sx={{ mt: 2, fontSize: 12 }}>
            O <b>padrão</b> vale para todo relatório que não escolheu um template. Mudar um template muda na hora os
            relatórios ligados a ele, inclusive os publicados.
          </Alert>
        </Box>

        {!template || !t ? (
          <Alert severity="info">Escolha um template na lista ou crie um novo.</Alert>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(420px, 1fr) minmax(480px, 1.2fr)' }, gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {doPortal && (
                <Alert severity="warning">
                  Este é o template embutido no portal e não pode ser alterado. Para mudar, use “Novo template”, que
                  começa com estes valores, e marque o novo como padrão.
                </Alert>
              )}
              <TextField size="small" label="Nome" value={template.nome} disabled={doPortal}
                         onChange={e => mudar({ nome: e.target.value })} inputProps={{ maxLength: 80 }} />
              <TextField size="small" label="Descrição" value={template.descricao ?? ''} disabled={doPortal} multiline maxRows={3}
                         onChange={e => mudar({ descricao: e.target.value })} inputProps={{ maxLength: 1000 }}
                         helperText="Vai no dicionário: diga ao Claude quando usar este template." />

              <Divider textAlign="left">Cores</Divider>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1.5 }}>
                {CORES.map(c => (
                  <CampoCor key={c.chave} rotulo={c.rotulo} dica={c.dica} valor={String(t[c.chave])}
                            aoMudar={v => mudarToken({ [c.chave]: v } as Partial<Tokens>)} />
                ))}
              </Box>

              <Divider textAlign="left">Paleta dos gráficos</Divider>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                {t.paleta.map((cor, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
                    <CampoCor rotulo={`Série ${i + 1}`} valor={cor}
                              aoMudar={v => mudarToken({ paleta: t.paleta.map((c, j) => (j === i ? v : c)) })} />
                    <IconButton size="small" disabled={t.paleta.length <= 1}
                                onClick={() => mudarToken({ paleta: t.paleta.filter((_, j) => j !== i) })}>×</IconButton>
                  </Box>
                ))}
                <Button size="small" disabled={t.paleta.length >= 12}
                        onClick={() => mudarToken({ paleta: [...t.paleta, '#64748b'] })}>+ cor</Button>
              </Box>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: -8 }}>
                Cada série recebe a cor na ordem. Evite duas cores vizinhas parecidas.
              </div>

              <Divider textAlign="left">Fonte e medidas</Divider>
              <TextField size="small" label="Fonte (pilha CSS)" value={t.fonte} onChange={e => mudarToken({ fonte: e.target.value })}
                         helperText="Ex.: 'Nunito Sans', 'Segoe UI', sans-serif" />
              <TextField size="small" label="Endereço da fonte (Google Fonts)" value={t.fonteUrl ?? ''}
                         onChange={e => mudarToken({ fonteUrl: e.target.value })}
                         helperText="https://fonts.googleapis.com/css2?family=... — vazio usa a fonte do sistema" />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField size="small" type="number" label="Arredondamento (px)" value={t.raio}
                           onChange={e => mudarToken({ raio: Number(e.target.value) })} inputProps={{ min: 0, max: 40 }} />
                <TextField size="small" type="number" label="Espaçamento (px)" value={t.espaco}
                           onChange={e => mudarToken({ espaco: Number(e.target.value) })} inputProps={{ min: 4, max: 40 }} />
              </Box>

              <Divider textAlign="left">Logo</Divider>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {template.logo
                  ? <img src={template.logo} alt="Logo" style={{ maxHeight: 40, maxWidth: 160, objectFit: 'contain' }} />
                  : <span style={{ fontSize: 13, color: '#64748b' }}>Sem logo</span>}
                <input ref={arquivoLogo} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden
                       onChange={e => lerLogo(e.target.files?.[0])} />
                <Button size="small" disabled={doPortal} onClick={() => arquivoLogo.current?.click()}>Escolher imagem</Button>
                {template.logo && <Button size="small" color="error" onClick={() => mudar({ logo: null })}>Tirar</Button>}
              </Box>

              <Divider textAlign="left">CSS dos componentes</Divider>
              <TextField multiline minRows={10} maxRows={24} value={template.css} disabled={doPortal}
                         onChange={e => mudar({ css: e.target.value })}
                         inputProps={{ style: { fontFamily: 'Consolas, monospace', fontSize: 12 } }}
                         helperText="Escreva sobre as variáveis (var(--pt-primaria)) e mantenha o prefixo pt- nas classes. Sem <style> nem <script>." />
              {!doPortal && cssDoPortal && template.css !== cssDoPortal && (
                <Box><Button size="small" onClick={() => mudar({ css: cssDoPortal })}>Restaurar o CSS do portal</Button></Box>
              )}

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {!doPortal && (
                  <Button variant="contained" className="btn-purple" disabled={salvando} onClick={salvar}>
                    {salvando ? 'Salvando…' : 'Salvar template'}
                  </Button>
                )}
                {template.id != null && !template.padrao && (
                  <Button variant="outlined" onClick={() => definirPadrao(template.id ?? null)}>Usar como padrão</Button>
                )}
                {doPortal && lista.some(l => l.id != null && l.padrao) && (
                  <Button variant="outlined" onClick={() => definirPadrao(null)}>Voltar a usar o do portal como padrão</Button>
                )}
                {template.id != null && <Button color="error" onClick={excluir}>Excluir</Button>}
              </Box>
            </Box>

            <Box sx={{ position: { xl: 'sticky' }, top: 16, alignSelf: 'start' }}>
              <h4 style={{ margin: '0 0 8px' }}>Prévia</h4>
              {erroPrevia && <Alert severity="error" sx={{ mb: 1 }}>{erroPrevia}</Alert>}
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                {previa
                  ? <RelatorioHtmlFrame html={previa.html} contexto={previa.contexto} altura={760}
                                        consultar={() => Promise.reject(new Error('A prévia do template não consulta dados.'))} />
                  : <div style={{ padding: 24, color: '#64748b' }}>Montando a prévia…</div>}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </div>
  )
}

export default TemplatesHtml
